import { groupBy } from 'lodash'
import { useState } from 'react'

import { Dayjs } from '@bemlo/date'
import { ShiftHours } from '@bemlo/enums'
import { Enumerable } from '@bemlo/utils'

import { Booking } from './Booking'
import { ExactTimeInput } from './ExactTimeInput'
import { ShiftHoursAvailabilityCell } from './ShiftHoursAvailabilityCell'
import { ResourceAvailability, ShiftBooking } from './types'
import { Color } from '../../../constants'
import { Button, Div, Flex, Text } from '../../../elements'

const { DAY, EVENING, NIGHT } = ShiftHours

type ModeSwitchProps = {
  isExact: boolean
  onSwitch: () => void
  isBooked: boolean
}

const ModeSwitch = (props: ModeSwitchProps) => {
  if (props.isBooked) return <Div />
  return (
    <Button
      size="xxs"
      design="transparent-light"
      icon={props.isExact ? 'SunIcon' : 'ClockIcon'}
      iconDesign="solid"
      onClick={props.onSwitch}
    />
  )
}

type RowProps = {
  day: Dayjs
  availability: ResourceAvailability[]
  booking: ShiftBooking | undefined
  onCreateExactAvailability: (startDateTime: Dayjs, endDateTime: Dayjs) => void
  onUpdateExactAvailability: (
    id: string,
    startDateTime: Dayjs,
    endDateTime: Dayjs,
  ) => void
  onMarkAsAvailable: (shiftHours: ShiftHours) => void
  onMarkAsUnavailable: (id: string) => void
  onDelete: (id: Enumerable<string>) => Promise<void>
  defaultTimes: {
    startTime: string
    endTime: string
  }
  setDefaultTime: (newDefault: { startTime: string; endTime: string }) => void
}

export const Row = (props: RowProps) => {
  const booking = props.booking

  const exactAvailability = props.availability.find(
    ({ shiftHours }) => !shiftHours,
  )
  const [isExact, setIsExact] = useState(!!exactAvailability)
  const [startTime, setStartTime] = useState(
    exactAvailability?.startDateTime.tz('utc').format('HH:mm') ??
      props.defaultTimes.startTime,
  )
  const [endTime, setEndTime] = useState(
    exactAvailability?.endDateTime.tz('utc').format('HH:mm') ??
      props.defaultTimes.endTime,
  )

  const {
    [DAY]: dayAvailability,
    [EVENING]: eveningAvailability,
    [NIGHT]: nightAvailability,
  } = groupBy(props.availability, 'shiftHours')

  const handleToggleExact = async () => {
    // Clear up any existing availability when switching between modes
    if (isExact) {
      const ids = props.availability.filter(({ shiftHours }) => !shiftHours)
      if (ids.length) {
        void props.onDelete(ids.map(({ id }) => id))
      }
      setIsExact(false)
    } else {
      const ids = props.availability
        .filter(({ shiftHours }) => shiftHours)
        .map(({ id }) => id)
      if (ids.length) {
        void props.onDelete(ids)
      }

      const defaultTimes = props.defaultTimes

      void props.onCreateExactAvailability(
        props.day.time(defaultTimes.startTime).startOf('minute'),
        props.day.time(defaultTimes.endTime).startOf('minute'),
      )
      setStartTime(defaultTimes.startTime)
      setEndTime(defaultTimes.endTime)
      setIsExact(true)
    }
  }

  const handleUpdateExact = () => {
    const exactAvailability = props.availability.find(
      ({ shiftHours }) => !shiftHours,
    )
    if (!exactAvailability) return

    const newStartDateTime = props.day.time(startTime).startOf('minute')
    let newEndDateTime = props.day.time(endTime).startOf('minute')

    if (
      newEndDateTime.isBefore(newStartDateTime) ||
      newEndDateTime.isSame(newStartDateTime)
    ) {
      newEndDateTime = newEndDateTime.add(1, 'day')
    }
    void props.onUpdateExactAvailability(
      exactAvailability.id,
      newStartDateTime,
      newEndDateTime,
    )
    props.setDefaultTime({ startTime, endTime })
  }

  return (
    <>
      <Flex col styles={{ justifyContent: 'center' }}>
        <Text color={Color.DARKEST_MEDIUM_GREY} styles={{ fontSize: 10 }}>
          {props.day.format('ddd')}
        </Text>
        <Text size={6} color={Color.CHARCOAL} bold>
          {props.day.format('DD')}
        </Text>
      </Flex>
      {booking ? (
        <Booking booking={booking} />
      ) : isExact ? (
        <ExactTimeInput
          startTime={startTime}
          endTime={endTime}
          onStartTimeChange={setStartTime}
          onEndTimeChange={setEndTime}
          onBlur={handleUpdateExact}
        />
      ) : (
        <>
          <ShiftHoursAvailabilityCell
            type={DAY}
            availability={dayAvailability?.[0]}
            onMarkAsAvailable={() => props.onMarkAsAvailable(DAY)}
            onMarkAsUnavailable={props.onMarkAsUnavailable}
            onDelete={props.onDelete}
          />
          <ShiftHoursAvailabilityCell
            type={EVENING}
            availability={eveningAvailability?.[0]}
            onMarkAsAvailable={() => props.onMarkAsAvailable(EVENING)}
            onMarkAsUnavailable={props.onMarkAsUnavailable}
            onDelete={props.onDelete}
          />
          <ShiftHoursAvailabilityCell
            type={NIGHT}
            availability={nightAvailability?.[0]}
            onMarkAsAvailable={() => props.onMarkAsAvailable(NIGHT)}
            onMarkAsUnavailable={props.onMarkAsUnavailable}
            onDelete={props.onDelete}
          />
        </>
      )}
      <ModeSwitch
        isBooked={!!booking}
        isExact={isExact}
        onSwitch={handleToggleExact}
      />
    </>
  )
}
